//core
import React, {useCallback, useState, useContext} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import arePropsEqual from "react-fast-compare";
import {
  Box,
  IconButton,
  Badge,
  Tooltip,
  useMediaQuery,
  Typography,
  Divider,
} from '@mui/material';

//styles
import { useStyles } from "./styles";

//icons
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SettingsIcon from '@mui/icons-material/Settings';
import RefreshIcon from "@mui/icons-material/Refresh";

//helpers
import { countValuesInObj } from "../../../../../helpers/countValuesInObj";
import { getCookie } from "../../../../../helpers/utils";

//routes
import routes from "../../../../../navigation/routes";

//context
import { GlobalContext } from "../../../../../context/GlobalContext";

//constants
import { cookieSupervision } from "../../../../../constants";

//store
import {
  selectDashboardStatisticChartsIsFetching,
  selectDashboardTopWidgetsIsFetching,
} from "../../../../../store/admin/dashboardV2/selectors";

//components
import MuiDrawerWrapper from "../../../../UI/MuiDrawerWrapper";
import PeriodDatesField from "../../../../UI/FormFields/PeriodDatesField";
import Filter from "../Filter";
import { initialDashboardV2FilterData, IPeriodsType } from "../../index";
import { IDashboardFilterType } from "../../types";
import Button from "../../../../UI/Buttons/Button";

interface IWidgetsDrawerFilterProps {
  initialFilter: IDashboardFilterType;
  setUpdateFilter: React.Dispatch<React.SetStateAction<IDashboardFilterType>>;
  periods: IPeriodsType;
  setPeriods: (data: IPeriodsType) => void;
  availableFields: string[];
  onRefresh: () => void;
  lastUpdate: string;
}

const WidgetsDrawerFilter: React.FC<IWidgetsDrawerFilterProps> = ({
  periods,
  setPeriods,
  initialFilter,
  setUpdateFilter,
  availableFields,
  onRefresh,
  lastUpdate,
}): JSX.Element => {
  const history = useNavigate();
  const { t } = useTranslation();
  const styles = useStyles();

  const mobile = useMediaQuery('(max-width:767px)');
  const isSupervision = JSON.parse(getCookie(cookieSupervision));

  const context = useContext(GlobalContext);

  const topWidgetsIsFetching = useSelector(selectDashboardTopWidgetsIsFetching)
  const statisticChartIsFetching = useSelector(selectDashboardStatisticChartsIsFetching)

  const [openDrawer, setOpenDrawer] = useState(false);

  const handleOpenDrawer = () => {
    setOpenDrawer(true);
  }

  const handleClearFilters = useCallback(() => {
    setUpdateFilter(initialDashboardV2FilterData);
    context.resetDashboardV2Filter();
  }, [setUpdateFilter]);

  const handleApplyPeriods = (data: any) => {
    setPeriods(data);
  }

  const handleClickSettings = () => {
    history(routes.dashboard.settings);
  }

  return (
    <>
      <Box className={styles.block}>
        <Box className={styles.first}>
          {!mobile ? (
            <PeriodDatesField
              periods={periods}
              onApply={handleApplyPeriods}
            />
          ) : (
            <Typography variant="h6">{t("common.breadcrumbs.overview")}</Typography>
          )}
        </Box>

        <Box className={styles.second}>
          <Box className={styles.actions}>
            {/*refresh data*/}
            {!mobile && (
              <Box className={styles.refresh}>
                <Typography variant="overline" noWrap>
                  {`${t("admin.overview.lastUpdate")}: ${lastUpdate}`}
                </Typography>

                <Button
                  sx={{ width: "max-content" }}
                  variant="outlined"
                  color="secondary"
                  startIcon={<RefreshIcon />}
                  disabled={topWidgetsIsFetching || statisticChartIsFetching}
                  onClick={onRefresh}
                >
                  {t("common.buttons.refresh")}
                </Button>
              </Box>
            )}

            {!mobile && <Divider className={styles.divider} orientation="vertical" flexItem />}

            {/*reset filter*/}
            <IconButton
              className={styles.iconButton}
              size={mobile ? 'small' : 'medium'}
              onClick={handleClearFilters}
              disabled={arePropsEqual(initialDashboardV2FilterData, initialFilter)}
            >
              <Tooltip title={t("common.buttons.reset_filters")} placement="bottom">
                <FilterAltOffIcon
                  color={arePropsEqual(initialDashboardV2FilterData, initialFilter) ? 'inherit' : 'primary'}
                />
              </Tooltip>
            </IconButton>

            {/*open filter*/}
            <IconButton
              className={styles.iconButton}
              size={mobile ? 'small' : 'medium'}
              onClick={handleOpenDrawer}
            >
              <Tooltip title={t("common.components.filters.title")} placement="bottom">
                <Badge
                  className={styles.badge}
                  badgeContent={countValuesInObj(initialFilter)}
                  color="secondary"
                >
                  <FilterAltIcon
                    color={!!countValuesInObj(initialFilter) ? 'primary' : 'inherit'}
                  />
                </Badge>
              </Tooltip>
            </IconButton>

            {!isSupervision && (<>
              <IconButton
                className={styles.iconButton}
                size={mobile ? 'small' : 'medium'}
                onClick={handleClickSettings}

              >
                <Tooltip title={t("common.buttons.settings")} placement="bottom">
                  <SettingsIcon color={'inherit'} />
                </Tooltip>
              </IconButton>
            </>)}
          </Box>
        </Box>
      </Box>

      {mobile && (
        <Box className={styles.mobileBlock}>
          <Box className={styles.refresh}>
            <Typography variant="overline" noWrap>
              {`${t("admin.overview.lastUpdate")}: ${lastUpdate}`}
            </Typography>

            <Button
              sx={{ width: "max-content" }}
              variant="outlined"
              color="secondary"
              startIcon={<RefreshIcon />}
              disabled={topWidgetsIsFetching || statisticChartIsFetching}
              onClick={onRefresh}
            >
              {t("common.buttons.refresh")}
            </Button>
          </Box>
          <PeriodDatesField
            periods={periods}
            onApply={handleApplyPeriods}
          />
        </Box>
      )}

      <MuiDrawerWrapper
        title={t("common.components.filters.title")}
        open={openDrawer}
        toggleDrawerState={setOpenDrawer}
      >
        <Filter
          initialFilter={initialFilter}
          setUpdateFilter={setUpdateFilter}
          availableFields={availableFields}
          setToggleDrawer={setOpenDrawer}
        />
      </MuiDrawerWrapper>
    </>
  );
};

export default WidgetsDrawerFilter;
